import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useRecentEngagements } from '../hooks/useRecentEngagements';
import { readFlag, writeFlag } from 'modules/Common/utils/storageFlag';
import { engagementPath } from '../utils/verifierPaths';

/** The sidebar's "Recent" sub-list under Engagements (W6). Hidden when the sidebar is collapsed. */
const RecentEngagementsNav = ({ isCollapsed }) => {
  const { t } = useTranslation('app');
  const { pathname } = useLocation();
  const { user } = useUser();
  const flagKey = `cg.verifier.recent.open.${user?.id}`;
  const [open, setOpen] = useState(() => readFlag(flagKey, true));
  const recent = useRecentEngagements();
  if (isCollapsed || recent.length === 0) return null;

  const toggle = () => {
    writeFlag(flagKey, !open);
    setOpen(!open);
  };

  return (
    <div className="ml-4 border-l border-slate-100 pl-2.5">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 px-2 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600"
      >
        {t('nav.verifier.recent', 'Recent')}
        <span className="font-normal text-slate-300">{recent.length}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`ml-auto h-2.5 w-2.5 transition-transform ${open ? '' : '-rotate-90'}`}
        />
      </button>
      {open ? (
        <ul className="m-0 list-none p-0">
          {recent.map((e) => {
            const to = engagementPath(e._id);
            const current = pathname === to || pathname.startsWith(`${to}/`);
            return (
              <li key={e._id}>
                <Link
                  to={to}
                  aria-current={current ? 'page' : undefined}
                  className={`flex items-center gap-2 rounded-md px-2 py-1 text-xs no-underline ${
                    current
                      ? 'bg-primaryColor/10 font-semibold text-primaryColor-dark'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {e.installationName} · {e.reportingYear}
                    <span className="font-normal text-slate-400"> — {e.operatorName}</span>
                  </span>
                  {e.openFindings ? (
                    <span
                      className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full border border-amber-200 bg-amber-50 px-1 text-[10px] font-bold text-amber-700"
                      title={t('verifier.engagement.findings', {
                        count: e.openFindings,
                        defaultValue: '{{count}} open finding',
                        defaultValue_other: '{{count}} open findings',
                      })}
                    >
                      {e.openFindings}
                    </span>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default RecentEngagementsNav;
